import React, { useEffect, useState, useRef } from "react";
import { getAllGrupos } from "../../../services/grupo.service";
import {
  registrarPlazoNotas,
  actualizarPlazoNotas,
  getAllPlazoNotas
} from "../../../services/plazoNotas.service";
import { getAllRegistrosByIdDocenteIdIdiomaIdGrupoEstado } from "../../../services/registro.service";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import {
  showErrorAlert,
  showActualizadoCorrectamenteAlert
} from "../../../components/molecules/ShowAlert";
import { EyeIcon } from "../../../assets/icons/Svgs";
import { PlazoNotasList } from "../../../modules/docente/PlazoNotasList";
import Swal from "sweetalert2";

export const PlazoNotas = () => {
  const [grupos, setGrupos] = useState([]);
  const [plazosActivos, setPlazosActivos] = useState([]);
  const [gruposCompletados, setGruposCompletados] = useState([]);
  const [showList, setShowList] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedGrupos, setSelectedGrupos] = useState([]);
  const [docenteNombre, setDocente] = useState("Sin asignar");
  const [cargando, setCargando] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const debounceRef = useRef(null);

  const [form, setForm] = useState({
    idGrupos: [],
    idDocentes: [],
    fechaInicio: "",
    fechaFin: ""
  });

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setCargando(true);
        
        const [gruposData, plazosData] = await Promise.all([
          getAllGrupos(),
          getAllPlazoNotas()
        ]);

        setGrupos(gruposData);
        
        const hoy = new Date();
        const activos = plazosData.filter(p => new Date(p.fechaFin) >= hoy);
        setPlazosActivos(activos);

        const completadosPromises = gruposData.map(async grupo => {
          try {
            if (!grupo.idDocente || !grupo.idIdioma || !grupo.idGrupo) {
              console.warn(`Grupo ${grupo.idGrupo} tiene campos faltantes`, grupo);
              return null;
            }

            const registros = await getAllRegistrosByIdDocenteIdIdiomaIdGrupoEstado(
              grupo.idDocente,
              grupo.idIdioma,
              grupo.idGrupo,
              "Progreso"
            ).catch(() => []);

            return registros.length === 0 ? grupo.idGrupo : null;
          } catch (error) {
            console.error(`Error verificando grupo ${grupo.idGrupo}:`, error);
            return null;
          }
        });

        const completados = (await Promise.all(completadosPromises)).filter(Boolean);
        setGruposCompletados(completados);
      } catch (error) {
        showErrorAlert({ text: "Error al cargar datos" });
      } finally {
        setCargando(false);
      }
    };
    
    cargarDatos();
  }, []);

  // Debounce searchTerm → debouncedTerm
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  // Filtrar grupos según el término de búsqueda
  const gruposFiltrados = grupos.filter(g => {
    if (!debouncedTerm) return true;
    const q = debouncedTerm.toLowerCase();
    const nombreGrupo = (g.nombre || '').toLowerCase();
    const nombreDocente = (g.docente?.nombre || '').toLowerCase();
    const apellidoDocente = (g.docente?.apellido || '').toLowerCase();
    const docenteCompleto = `${nombreDocente} ${apellidoDocente}`.trim();
    const moduloNombre = (g.modulo?.nombre || '').toLowerCase();
    const nivelNombre = (g.nivel?.nombre || '').toLowerCase();
    
    return (
      nombreGrupo.includes(q) ||
      nombreDocente.includes(q) ||
      apellidoDocente.includes(q) ||
      docenteCompleto.includes(q) ||
      moduloNombre.includes(q) ||
      nivelNombre.includes(q)
    );
  });

  const toggleGrupo = (grupo) => {
    if (gruposCompletados.includes(grupo.idGrupo)) {
      showErrorAlert({
        text: "Este grupo ya completó el curso. No se puede asignar nuevo plazo.",
        icon: "warning"
      });
      return;
    }

    const tienePlazoActivo = plazosActivos.some(p => p.idGrupo === grupo.idGrupo);
    if (tienePlazoActivo) {
      showErrorAlert({
        text: "Este grupo ya tiene un plazo activo.",
        icon: "warning"
      });
      return;
    }

    setSelectedGrupos(prev => {
      const exists = prev.some(g => g.idGrupo === grupo.idGrupo);
      return exists
        ? prev.filter(g => g.idGrupo !== grupo.idGrupo)
        : [...prev, grupo];
    });
  };

  useEffect(() => {
    const idGrupos = selectedGrupos.map(g => g.idGrupo);
    const idDocentes = selectedGrupos.map(g => g.idDocente || "");

    setForm(prev => ({
      ...prev,
      idGrupos,
      idDocentes
    }));

    setDocente(
      selectedGrupos.length === 0
        ? "Sin asignar"
        : selectedGrupos.length === 1
        ? `${selectedGrupos[0]?.docente?.nombre || ""} ${selectedGrupos[0]?.docente?.apellido || ""}`.trim() || "Sin asignar"
        : `${selectedGrupos.length} grupos seleccionados`
    );
  }, [selectedGrupos]);

  // Cerrar dropdown al hacer click fuera y limpiar búsqueda
  useEffect(() => {
    if (!dropdownOpen) {
      setSearchTerm('');
      return;
    }
    const onClickOutside = (e) => {
      const target = e.target;
      const dropdown = document.querySelector('[data-dropdown="grupos"]');
      if (dropdown && !dropdown.contains(target)) {
        setDropdownOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, [dropdownOpen]);

  const handleChange = ({ target }) => {
    setForm(prev => ({ ...prev, [target.name]: target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { idGrupos, fechaInicio, fechaFin } = form;

    if (!idGrupos.length || !fechaInicio || !fechaFin) {
      showErrorAlert({ text: "Complete todos los campos requeridos" });
      return;
    }

    if (new Date(fechaFin) <= new Date(fechaInicio)) {
      showErrorAlert({ text: "La fecha final debe ser posterior a la inicial" });
      return;
    }

    const loadingAlert = Swal.fire({
      title: 'Procesando plazos...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const resultados = await Promise.allSettled(
        idGrupos.map(async (idGrupo) => {
          const grupo = grupos.find(g => g.idGrupo === idGrupo);
          const plazoExistente = plazosActivos.find(p => p.idGrupo === idGrupo);

          const datos = {
            idGrupo,
            idDocente: grupo?.idDocente || "",
            fechaInicio,
            fechaFin
          };

          if (plazoExistente) {
            await actualizarPlazoNotas(plazoExistente.idPlazoNotas, datos);
            return { id: idGrupo, nombre: grupo.nombre, tipo: "Extensión" };
          } else {
            await registrarPlazoNotas(datos);
            return { id: idGrupo, nombre: grupo.nombre, tipo: "Nuevo" };
          }
        })
      );

      const exitosos = resultados.filter(r => r.status === "fulfilled");
      const errores = resultados.filter(r => r.status === "rejected");

      setSelectedGrupos([]);
      await loadingAlert.close();

      showActualizadoCorrectamenteAlert({
        text: `Procesados: ${exitosos.length} | Errores: ${errores.length}`
      });

      const [nuevosPlazos] = await Promise.all([
        getAllPlazoNotas(),
        getAllGrupos()
      ]);
      
      const hoy = new Date();
      setPlazosActivos(nuevosPlazos.filter(p => new Date(p.fechaFin) >= hoy));

    } catch (error) {
      await loadingAlert.close();
      showErrorAlert({
        text: error.response?.data?.message || "Error al procesar plazos"
      });
    }
  };

  return (
    <ContenedorPageTemplate titulo="Registrar o Extender Plazos de Subida de Notas">
      {cargando ? (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-base text-gray-600">Cargando datos...</p>
        </div>
      ) : (
        <div className="container mx-auto px-4 py-6 max-w-6xl">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            
            {/* Header elegante */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white flex items-center">
                    <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Gestión de Plazos de Notas
                  </h1>
                  <p className="text-base text-blue-100 mt-1">Configura fechas límite para la subida de calificaciones</p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium">
                    {grupos.length} grupos disponibles
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowList(v => !v)}
                    className={`p-3 rounded-lg transition-all ${
                      showList 
                        ? 'bg-blue-400 text-white' 
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                    title={showList ? "Ocultar plazos" : "Mostrar plazos"}
                  >
                    <EyeIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Sección de selección de grupos */}
                <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center">
                      <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Seleccionar Grupos
                    </h2>
                    {selectedGrupos.length > 0 && (
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                        {selectedGrupos.length} seleccionado{selectedGrupos.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {/* Dropdown mejorado */}
                  <div className="relative" onClick={e => e.stopPropagation()} data-dropdown="grupos">
                    <div
                      className={`w-full border-2 rounded-lg cursor-pointer bg-white transition-all ${
                        dropdownOpen ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
                      }`}
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                      <div className="p-4">
                        <p className="text-sm text-gray-600 mb-1">Grupos seleccionados</p>
                        <p className={`${selectedGrupos.length ? 'text-gray-800' : 'text-gray-400'} text-base font-medium`}>
                          {selectedGrupos.length > 0
                            ? selectedGrupos.map(g => g.nombre).join(", ")
                            : "Haz clic para seleccionar grupos"}
                        </p>
                        {selectedGrupos.length > 0 && (
                          <p className="text-sm text-green-600 mt-1">
                            ✅ Listo para configurar plazos
                          </p>
                        )}
                      </div>
                      <div className="absolute top-4 right-4">
                        <svg
                          className={`w-6 h-6 transform transition-transform ${
                            dropdownOpen ? 'rotate-180 text-blue-600' : 'text-gray-500'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                        </svg>
                      </div>
                    </div>

                    {dropdownOpen && (
                      <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
                        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                          <p className="text-sm font-medium text-gray-700 mb-3">Grupos disponibles</p>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                              </svg>
                            </div>
                            <input
                              type="text"
                              placeholder="Buscar por nombre, docente, módulo o nivel..."
                              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
                              value={searchTerm}
                              onChange={e => setSearchTerm(e.target.value)}
                              onClick={e => e.stopPropagation()}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            💡 Los grupos con plazo activo o completados no se pueden seleccionar
                          </p>
                        </div>
                        
                        <div className="max-h-64 overflow-y-auto">
                          {gruposFiltrados.length > 0 ? (
                            gruposFiltrados.map(g => {
                            const tienePlazo = plazosActivos.some(p => p.idGrupo === g.idGrupo);
                            const completado = gruposCompletados.includes(g.idGrupo);
                            const deshabilitado = tienePlazo || completado;
                            const seleccionado = selectedGrupos.some(sg => sg.idGrupo === g.idGrupo);

                            return (
                              <div
                                key={g.idGrupo}
                                className={`p-3 border-b border-gray-100 last:border-b-0 transition-all ${
                                  deshabilitado 
                                    ? 'bg-gray-50 cursor-not-allowed' 
                                    : seleccionado
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'hover:bg-blue-50 cursor-pointer'
                                }`}
                                onClick={() => !deshabilitado && toggleGrupo(g)}
                              >
                                <div className="flex items-start gap-3">
                                  <div
                                    className={`mt-1 h-5 w-5 border-2 rounded-full flex items-center justify-center ${
                                      seleccionado 
                                        ? 'bg-blue-600 border-blue-600' 
                                        : deshabilitado
                                        ? 'border-gray-300'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {seleccionado && (
                                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/>
                                      </svg>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h3 className={`text-base font-semibold ${
                                      deshabilitado ? 'text-gray-400' : 'text-gray-800'
                                    }`}>
                                      {g.nombre}
                                    </h3>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                        {g.modulo?.nombre || 'Sin módulo'}
                                      </span>
                                      <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                        {g.nivel?.nombre || 'Sin nivel'}
                                      </span>
                                    </div>
                                    <p className={`text-sm mt-1 ${
                                      deshabilitado ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                      👨‍🏫 {g.docente?.nombre || "Sin docente"}
                                    </p>
                                    {tienePlazo && (
                                      <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                        ⏰ Plazo activo
                                      </span>
                                    )}
                                    {completado && (
                                      <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 mt-1">
                                        ✅ Completado
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                          ) : (
                            <div className="p-6 text-center">
                              <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p className="text-sm font-medium text-gray-500">
                                {debouncedTerm ? 'No se encontraron grupos' : 'No hay grupos disponibles'}
                              </p>
                              {debouncedTerm && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Intenta con otro término de búsqueda
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sección de información del docente */}
                {selectedGrupos.length > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center mb-4">
                      <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Información del Docente
                    </h2>
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <p className="text-base font-medium text-gray-800">{docenteNombre}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedGrupos.length === 1 
                          ? 'Docente asignado al grupo seleccionado'
                          : `${selectedGrupos.length} grupos seleccionados`
                        }
                      </p>
                    </div>
                  </div>
                )}

                {/* Sección de fechas */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center mb-4">
                    <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Configurar Fechas del Plazo
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Fecha de Inicio *
                      </label>
                      <input
                        type="date"
                        name="fechaInicio"
                        value={form.fechaInicio}
                        onChange={handleChange}
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        required
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Fecha de Fin *
                      </label>
                      <input
                        type="date"
                        name="fechaFin"
                        value={form.fechaFin}
                        onChange={handleChange}
                        className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        required
                        min={form.fechaInicio}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4 bg-blue-100 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-blue-800">Información importante</p>
                        <p className="text-sm text-blue-700 mt-1">
                          La planilla se habilita 2 días hábiles después de la fecha de fin del grupo.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botón de envío */}
                <div className="flex items-center justify-end space-x-4 pt-6">
                  <button
                    type="submit"
                    disabled={!selectedGrupos.length || !form.fechaInicio || !form.fechaFin}
                    className={`px-8 py-4 rounded-xl font-semibold text-lg flex items-center transition-all duration-300 ${
                      !selectedGrupos.length || !form.fechaInicio || !form.fechaFin
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg hover:from-blue-700 hover:to-blue-600 hover:shadow-xl transform hover:-translate-y-1'
                    }`}
                  >
                    <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {selectedGrupos.length > 1 
                      ? `Configurar ${selectedGrupos.length} Plazos`
                      : 'Configurar Plazo'
                    }
                  </button>
                </div>
              </form>

              {showList && (
                <div className="mt-8">
                  <PlazoNotasList />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </ContenedorPageTemplate>
  );
};

export default PlazoNotas;